/**
 * Avatar utility functions with caching support
 */

/**
 * Get the public avatar URL for a user
 * Avatar endpoint is public - no authentication required
 * 
 * @param userId - The user ID
 * @param cacheBust - Optional cache-busting parameter (e.g., timestamp)
 * @returns The avatar URL
 */
export function getAvatarUrl(userId: string | undefined, cacheBust?: string | number): string | undefined {
  if (!userId) return undefined;
  
  const baseUrl = `/api/v1/users/${userId}/avatar`;
  
  // Add cache-busting parameter if provided
  if (cacheBust) {
    return `${baseUrl}?v=${cacheBust}`;
  }
  
  return baseUrl;
}

/**
 * Get avatar URL with current timestamp as cache buster
 * Use this when you know the avatar has just been updated
 */
export function getAvatarUrlFresh(userId: string | undefined): string | undefined {
  return getAvatarUrl(userId, Date.now());
}

/**
 * Check if a user has an avatar by attempting to load it
 * Since the endpoint returns 404 if no avatar exists, we can use this
 * to conditionally show avatar vs fallback
 */
export async function hasAvatar(userId: string): Promise<boolean> {
  try {
    const response = await fetch(getAvatarUrl(userId) || '', { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Avatar image component props helper
 * Returns undefined if no userId, otherwise returns the avatar URL
 * The Avatar component will handle showing the fallback (initials)
 */
export function getAvatarProps(userId: string | undefined, name?: string, cacheBust?: string | number) {
  return {
    src: getAvatarUrl(userId, cacheBust),
    name: name,
    showFallback: true,
  };
}
